import { kWeightingFilters } from './loudness';

/**
 * Momentary loudness for a channel strip.
 *
 * The master gets the full treatment — momentary, short-term, integrated,
 * gated — because that is the number a mix is judged by. A channel strip wants
 * something different and much cheaper: *how loud is this track right now*, so a
 * balance can be set by number instead of by peak. Peak meters are bad at
 * exactly this; two tracks peaking identically can sit ten decibels apart.
 *
 * The arithmetic is split across the thread boundary on purpose:
 *
 * - **In the worklet**, each block is K-weighted and reduced to a single mean
 *   square per hundred milliseconds. One number, ten times a second, per track.
 *   Posting raw blocks instead would be four hundred messages a second for every
 *   track on the desk, which is the reason this class exists at all.
 * - **Here**, those numbers are assembled into a 400ms window and turned into
 *   LUFS. That is the part worth testing, so it is the part that stays out of
 *   the worklet.
 *
 * The filter *coefficients* are computed by `kWeightingFilters` and handed to the
 * worklet, so there is still only one definition of what K-weighting is; the
 * worklet holds only the recurrence that applies them.
 */

const LOUDNESS_OFFSET = -0.691;

/** Momentary loudness is a 400ms window, which is four of the pieces. */
const WINDOW_PIECES = 4;

/** How long each piece is. Ten a second is smooth enough for the eye. */
export const STRIP_PIECE_SEC = 0.1;

/**
 * Everything the worklet needs to do its half of the work, computed here so the
 * weighting has one definition.
 */
export function stripTapOptions(sampleRate: number): {
  shelf: ReturnType<typeof kWeightingFilters>[0];
  highPass: ReturnType<typeof kWeightingFilters>[1];
  pieceLength: number;
} {
  const [shelf, highPass] = kWeightingFilters(sampleRate);
  return { shelf, highPass, pieceLength: Math.max(1, Math.round(STRIP_PIECE_SEC * sampleRate)) };
}

/** Loudness of one strip, assembled from the pieces the worklet posts. */
export class StripLoudness {
  private readonly pieces: number[] = [];
  private peak = 0;

  /** Accepts one piece: the summed mean square of the weighted channels. */
  push(meanSquare: number, peak: number): void {
    if (!Number.isFinite(meanSquare) || meanSquare < 0) return;
    this.pieces.push(meanSquare);
    if (this.pieces.length > WINDOW_PIECES) this.pieces.shift();
    if (peak > this.peak) this.peak = peak;
  }

  /** Momentary loudness, in LUFS. Negative infinity before anything arrives. */
  momentaryLufs(): number {
    if (this.pieces.length === 0) return -Infinity;
    let sum = 0;
    for (const piece of this.pieces) sum += piece;
    const mean = sum / this.pieces.length;
    return mean <= 0 ? -Infinity : LOUDNESS_OFFSET + 10 * Math.log10(mean);
  }

  /** Highest sample seen since the last reset, in dB relative to full scale. */
  peakDb(): number {
    return this.peak > 0 ? 20 * Math.log10(this.peak) : -Infinity;
  }

  reset(): void {
    this.pieces.length = 0;
    this.peak = 0;
  }
}

/**
 * The same measurement done here rather than in a worklet, for tests and for any
 * caller holding finished audio.
 *
 * It exists so the split can be checked: feeding this the same audio a worklet
 * would see must produce the same number, and that is what the tests assert.
 */
export function stripPieces(
  channels: readonly Float32Array[],
  sampleRate: number,
): { meanSquare: number; peak: number }[] {
  const { shelf, highPass, pieceLength } = stripTapOptions(sampleRate);
  const states = channels.map(() => [
    { x1: 0, x2: 0, y1: 0, y2: 0 },
    { x1: 0, x2: 0, y1: 0, y2: 0 },
  ]);

  const out: { meanSquare: number; peak: number }[] = [];
  let sum = 0;
  let peak = 0;
  let count = 0;

  const length = channels[0]?.length ?? 0;
  for (let i = 0; i < length; i++) {
    for (let c = 0; c < channels.length; c++) {
      const raw = channels[c][i];
      if (Math.abs(raw) > peak) peak = Math.abs(raw);
      const value = biquad(highPass, states[c][1], biquad(shelf, states[c][0], raw));
      sum += value * value;
    }
    count++;
    if (count >= pieceLength) {
      out.push({ meanSquare: sum / count, peak });
      sum = 0;
      count = 0;
    }
  }
  return out;
}

function biquad(
  filter: { b0: number; b1: number; b2: number; a1: number; a2: number },
  state: { x1: number; x2: number; y1: number; y2: number },
  x0: number,
): number {
  const y0 =
    filter.b0 * x0 + filter.b1 * state.x1 + filter.b2 * state.x2 - filter.a1 * state.y1 - filter.a2 * state.y2;
  state.x2 = state.x1;
  state.x1 = x0;
  state.y2 = state.y1;
  state.y1 = y0;
  return y0;
}
